import { parseDate } from 'utils/format';
import { apiClientHeadRequest, apiClientPostRequest } from './backendRequest';

// Expiry is compared with the device clock, so on a device whose clock runs ahead a
// token looks expired right after its refresh. Storing a refreshed token creates a new
// auth object and consumers ask again, which once became tens of refreshes per second.
// Valid or not locally, a token the backend returned moments ago is good to use.
const REFRESH_COOLDOWN_MS = 60 * 1000;

let lastRefresh = null;

const sharedRefresh = (sessionToken, refresh, onUpdate) => {
  const isRecent = lastRefresh?.sessionToken === sessionToken
    && Math.abs(Date.now() - lastRefresh.startedAt) < REFRESH_COOLDOWN_MS;
  if (isRecent) {
    return lastRefresh.result;
  }

  const result = refresh().then((response) => {
    if (response.data) {
      onUpdate(response.data);
    } else if (lastRefresh?.result === result) {
      lastRefresh = null;
    }
    return response;
  });
  lastRefresh = { sessionToken, startedAt: Date.now(), result };

  return result;
};

const ancestryAuth = (userData, onUpdate = () => { }) => {
  /**
   * Check if current access_token is still valid.
   * Token expires in 60 minutes.
   * @returns {boolean}
   */
  const isTokenValid = () => {
    if (userData.ancestry_token?.access_token) {
      const expiresIn = userData.ancestry_token?.expires_in;
      if (expiresIn) {
        return parseDate(expiresIn) > Date.now();
      }
    }
    return false;
  };

  /**
   * Check if current access_token is at least refreshable by backend service.
   * Refresh is possible in 7 days.
   * @returns {boolean}
   */
  const isTokenRefreshable = () => {
    const refreshableExpiresIn = userData.ancestry_token?.refresh_expires_in;
    if (refreshableExpiresIn) {
      return parseDate(refreshableExpiresIn) > Date.now();
    }
    return false;
  };

  const refreshToken = async () => {
    try {
      const response = await apiClientPostRequest(
        'api/v1/ancestry/refresh',
        {},
        {
          Authorization: `Token ${userData.access_token}`,
        },
      );

      const json = await response.json();

      return {
        data: json.data,
      };
    } catch (e) {
      return { error: e };
    }
  };

  const isFamilysearchTokenValid = async () => {
    try {
      const response = await apiClientHeadRequest(
        'api/v1/familysearch/trees',
        {
          Authorization: `Token ${userData.access_token}`,
        },
      );

      if (response.ok) {
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  };

  return {
    getToken: async (type = "ancestry") => {
      if (type === "familysearch") {
        if (userData.familysearch_token?.access_token) {
          const isValid = await isFamilysearchTokenValid();
          if (isValid) {
            return { data: userData.familysearch_token.access_token };
          }
        }
        return { error: { code: 401, body: 'FamilySearch Token is not valid.' } };
      }

      if (isTokenValid()) {
        return { data: userData.ancestry_token.access_token };
      }

      if (isTokenRefreshable()) {
        const { data, error } = await sharedRefresh(userData.access_token, refreshToken, onUpdate);
        if (data) {
          return { data: data.access_token };
        }
        return { error };
      }

      return { error: { code: 401, body: 'Ancestry Token is not valid.' } };
    },
  };
};

export default ancestryAuth;
